import React, { useState } from 'react';
import './Footer.css';
import FooterLogo from '../assets/images/footerLogo.svg';
import emailjs from '@emailjs/browser';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Footer = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [errors, setErrors] = useState({});
  
  // Scroll animations
  const [sectionRef, isSectionVisible] = useScrollAnimation(0.6);
  const [logoRef, isLogoVisible] = useScrollAnimation(0.7);
  const [formRef, isFormVisible] = useScrollAnimation(0.8);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailBlur = (e) => {
    const { value } = e.target;
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration - you'll need to replace these with your actual values
      const SERVICE_ID = 'service_3ufgjci';
      const TEMPLATE_ID = 'template_7bkgmaj';
      const PUBLIC_KEY = 'jy1noTDCQOp5yeOyz'; // Replace with your EmailJS public key

      // Prepare template parameters
      const templateParams = {
        from_name: formData.fullName,
        from_email: formData.email,
        to_email: 'ptailor2510@gmail.com', // Your business email
        message: `New quote request from ${formData.fullName} (${formData.email})`
      };

      // Send email using EmailJS
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

      setSubmitStatus('success');
      setFormData({ fullName: '', email: '' }); // Reset form

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');

      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer ref={sectionRef} className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Left Column - Company Information */}
          <div className="footer-left">
            <div className="company-info">
              <div ref={logoRef} className={`logo-section ${isLogoVisible ? 'visible' : ''}`}>
                <div className="logo-icon">
                  <img src={FooterLogo} alt="Indus Trade Logo" className={`footer-logo image-reveal ${isLogoVisible ? 'visible' : ''}`} />
                </div>
              </div>
              <p className={`company-description text-reveal stagger-1 ${isLogoVisible ? 'visible' : ''}`}>
              25+ years of trusted craftsmanship, delivering authentic Indian furniture to global markets.</p>
            </div>
          </div>

          {/* Right Column - Interactive and Contact Information */}
          <div className="footer-right">
            {/* Request Your Quote Section */}
            <div ref={formRef} className={`quote-section ${isFormVisible ? 'visible' : ''}`}>
              <h4 className={`section-title text-reveal ${isFormVisible ? 'visible' : ''}`}>Request Your Quote</h4>
              <form className={`quote-form ${isFormVisible ? 'visible' : ''}`} onSubmit={handleSubmit}>
                <div className="form-row">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="FULL NAME"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                    required
                    disabled={isSubmitting}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="EMAIL ADDRESS"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    required
                    disabled={isSubmitting}
                  />
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="button-content">
                        <span className="loading-spinner"></span>
                        SENDING...
                      </span>
                    ) : (
                      'SUBMIT'
                    )}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="status-message success-message">
                    ✓ Thank you! Your quote request has been sent successfully.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="status-message error-message">
                    ✗ Sorry, there was an error sending your request. Please try again.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Us and Location in single row */}
            <div className="contact-location-row">
              {/* Contact Us Section */}
              <div className="contact-section">
                <h4 className="section-title">Contact Us</h4>
                <div className="contact-details">
                  <p className="contact-item">+91 98290 22729</p>
                  <a 
                    href="mailto:industradejodhpur@gmail.com" 
                    className="contact-item contact-link"
                  >
                    industradejodhpur@gmail.com
                  </a>
                </div>
              </div>

              {/* Location Section */}
              <div className="location-section">
                <h4 className="section-title">Location</h4>
                <div className="location-details">
                  <a 
                    href="https://maps.app.goo.gl/iSzB31Excz12bDQS6?g_st=ipc" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="location-link"
                  >
                    <p className="location-text">
                      Tanawara Phanta, Salawas Road,{'\n'}P.O. - Salawas, Jodhpur, Rajasthan, INDIA
                    </p>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">© Indus Trade 2025. All Rights Reserved.</p>
            <div className="social-links">
              <a href="#" className="social-link">Instagram</a>
              <a href="#" className="social-link">LinkedIn</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;